import { motion } from "framer-motion";
import { ArrowDownRight, CalendarClock, CalendarMinus, PiggyBank, Receipt, TrendingDown, Wallet } from "lucide-react";
import { MainCard } from "src/components/main-card-type.tsx";
import type { Schedule } from "../utils/amortization";
import { formatBRL, formatMonths, formatMonthYearLong, formatPercent } from "../utils/format";
import { SimulatorCharts, SimulatorPieCharts, SimulatorScenarioCharts } from "./AmortizacaoCharts";
import { AmortizacaoTable } from "./AmortizacaoTable";
import { CreditBanner } from "./CreditBanner";

interface Props {
  results: AmortizacaoResultType;
  onAddPlan?: () => void;
}

interface Metric {
  icon: typeof Wallet;
  label: string;
  tone: string;
  value: (s: Schedule) => string;
  betterLower?: boolean;
}

function ResultSummary({ baseline, withExtra, system }: { baseline: Schedule; withExtra: Schedule | null; system: string }) {
  const metrics: Metric[] = [
    { icon: Wallet, label: "Valor financiado", tone: "text-primary", value: (s) => formatBRL(s.principal) },
    { icon: Receipt, label: "Total de juros", tone: "text-destructive", value: (s) => formatBRL(s.totalInterest), betterLower: true },
    { icon: TrendingDown, label: "Total pago", tone: "text-foreground", value: (s) => formatBRL(s.totalPaid), betterLower: true },
    { icon: ArrowDownRight, label: "Primeira parcela", tone: "text-foreground", value: (s) => formatBRL(s.firstPayment) },
    { icon: ArrowDownRight, label: system === "SAC" ? "Última parcela" : "Valor da parcela", tone: "text-foreground", value: (s) => formatBRL(s.lastPayment) },
    { icon: CalendarClock, label: "Prazo total", tone: "text-foreground", value: (s) => formatMonths(s.termMonths), betterLower: true },
  ];

  if (!withExtra) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {metrics.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="rounded-xl border border-border bg-card p-4 shadow-sm"
          >
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <c.icon className={`h-4 w-4 ${c.tone}`} />
              {c.label}
            </div>
            <p className="text-lg font-bold tracking-tight">{c.value(baseline)}</p>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-md">
      <div className="grid grid-cols-[1.4fr_1fr_1fr] items-center gap-1 border-b border-border bg-muted/40 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:gap-2 sm:px-5 sm:text-sm">
        <span>Resumo</span>
        <span className="text-right">Sem amort.</span>
        <span className="text-right text-[hsl(var(--success))]">Com amort.</span>
      </div>
      <div className="divide-y divide-border">
        {metrics.map((m) => {
          const base = m.value(baseline);
          const extra = m.value(withExtra);
          const changed = m.betterLower && base !== extra;
          return (
            <div key={m.label} className="grid grid-cols-[1.4fr_1fr_1fr] items-center gap-1 px-3 py-2.5 sm:gap-2 sm:px-5 sm:py-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground sm:text-sm">
                <m.icon className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${m.tone}`} />
                <span className="leading-tight">{m.label}</span>
              </div>
              <p className="text-right text-xs font-semibold tabular-nums sm:text-sm">{base}</p>
              <p className={`text-right text-xs font-bold tabular-nums sm:text-sm ${changed ? "text-[hsl(var(--success))]" : "text-foreground"}`}>
                {extra}
              </p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function ComparisonPanel({ results }: { results: AmortizacaoResultType }) {
  const { comparison, withExtra, extraMode, startDate } = results;
  if (!comparison || !withExtra) return null;

  const payoffDate = formatMonthYearLong(startDate, withExtra.termMonths - 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border border-[hsl(var(--success)/0.3)] bg-gradient-to-br from-[hsl(var(--success)/0.08)] to-[hsl(var(--success)/0.04)] p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]">
          <PiggyBank className="h-5 w-5" />
        </span>
        <h3 className="text-xl font-bold">Impacto da amortização extra</h3>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <TrendingDown className="h-3.5 w-3.5" /> Economia de juros
          </div>
          <p className="mt-1 text-xl font-bold text-[hsl(var(--success))] sm:text-2xl">{formatBRL(comparison.interestSaved)}</p>
          <p className="text-sm text-muted-foreground">
            {formatPercent(comparison.interestSavedPercent, 1)} a menos em juros
          </p>
        </div>

        {extraMode === "term" ? (
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <CalendarMinus className="h-3.5 w-3.5" /> Prazo reduzido
            </div>
            <p className="mt-1 text-xl font-bold sm:text-2xl">{formatMonths(comparison.monthsSaved)}</p>
            <p className="text-sm text-muted-foreground">a menos de financiamento</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <CalendarMinus className="h-3.5 w-3.5" /> Redução na parcela
            </div>
            <p className="mt-1 text-xl font-bold sm:text-2xl">{formatBRL(comparison.paymentReduction)}</p>
            <p className="text-sm text-muted-foreground">na última parcela</p>
          </div>
        )}

        <div>
          <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <PiggyBank className="h-3.5 w-3.5" /> Total economizado
          </div>
          <p className="mt-1 text-xl font-bold sm:text-2xl">{formatBRL(comparison.totalPaidSaved)}</p>
          <p className="text-sm text-muted-foreground">no valor total pago</p>
        </div>
      </div>

      <p className="mt-4 rounded-xl border border-[hsl(var(--success)/0.3)] bg-white/60 p-3 text-sm leading-relaxed sm:text-base">
        O financiamento será quitado em{" "}
        <strong className="capitalize">{payoffDate}</strong>, proporcionando uma economia de{" "}
        <strong className="text-[hsl(var(--success))]">{formatBRL(comparison.totalPaidSaved)}</strong>
        {comparison.monthsSaved > 0 && (
          <> e redução de <strong>{comparison.monthsSaved}</strong>{" "}
          {comparison.monthsSaved === 1 ? "parcela" : "parcelas"}</>
        )}
        . O montante total a ser pago será de{" "}
        <strong>{formatBRL(withExtra.totalPaid)}</strong>.
      </p>
    </motion.div>
  );
}

export function AmortizacaoResponse({ results, onAddPlan }: Props) {
  return (
    <MainCard noPadding noBorder className="bg-transparent rounded-none mt-4">
      <div className="space-y-8">
        <ResultSummary baseline={results.baseline} withExtra={results.withExtra} system={results.system} />

        {results.comparison && results.withExtra && <ComparisonPanel results={results} />}

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <SimulatorPieCharts baseline={results.baseline} withExtra={results.withExtra} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
          <SimulatorScenarioCharts baseline={results.baseline} withExtra={results.withExtra} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}>
          <SimulatorCharts baseline={results.baseline} withExtra={results.withExtra} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}>
          <CreditBanner />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}>
          <AmortizacaoTable baseline={results.baseline} withExtra={results.withExtra} onAddPlan={onAddPlan} />
        </motion.div>
      </div>
    </MainCard>
  );
}
